import React from 'react';

console.log('=== App.jsx FILE LOADED ===');
console.log('Current URL:', typeof window !== 'undefined' ? window.location.href : 'server-side');
console.log('Current pathname:', typeof window !== 'undefined' ? window.location.pathname : 'server-side');
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AdminDashboard from './components/AdminDashboard';
import MetricsDashboard from './components/MetricsDashboard';
import ChatPage from './components/ChatPage';
import MyAppsPage from './components/MyAppsPage';
import SignPdfPage from './components/tools/SignPdfPage';
import PdfToolsPage from './components/tools/PdfToolsPage';
import FileConvertPage from './components/tools/FileConvertPage';
import Login from './components/Login';
import Register from './components/Register';
import VerifyEmail from './components/VerifyEmail';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import LandingPage from './landing/LandingPage.jsx';
import authService from './utils/auth.js';
import SubscriptionPaywall from './components/SubscriptionPaywall.jsx';
import NovaLoader from './components/NovaLoader.jsx';
import { ConfirmHost } from './components/ConfirmDialog.jsx';

// Protected Route Component
function ProtectedRoute({ children }) {
  const isAuthenticated = authService.isAuthenticated();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

// Public landing route: if already authenticated, send user to the app
function LandingRoute({ children }) {
  const isAuthenticated = authService.isAuthenticated();
  return isAuthenticated ? <Navigate to="/chat" replace /> : children;
}

// Admin-only Route Component (allows admin or super admin)
function AdminRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = React.useState(false);
  const [isChecking, setIsChecking] = React.useState(true);
  const [paywallOnly, setPaywallOnly] = React.useState(false);

  React.useEffect(() => {
    console.log('AdminRoute: Starting access check');
    const checkAccess = async () => {
      const authenticated = authService.isAuthenticated();
      console.log('AdminRoute: isAuthenticated', authenticated);
      setIsAuthenticated(authenticated);
      
      if (!authenticated) {
        console.log('AdminRoute: Not authenticated, redirecting to login');
        setIsChecking(false);
        return;
      }

      // Check admin and super admin status - verify with backend
      try {
        const profile = await authService.getProfile();
        const user = profile?.data?.user || profile?.user;
        const role = user?.effectiveRole || user?.role || 'user';
        const superAdminStatus = role === 'superadmin';
        const adminStatus = user?.hasEffectiveAdminAccess === true;

        const needsPaywall =
          user?.requiresPaywall === true &&
          user?.hasEffectiveAdminAccess === false &&
          user?.tenantBypassSubscription !== true;

        if (needsPaywall) {
          setPaywallOnly(true);
          setIsAdmin(false);
          setIsSuperAdmin(false);
        } else {
          setPaywallOnly(false);
          setIsAdmin(adminStatus);
          setIsSuperAdmin(superAdminStatus);
        }
        console.log('AdminRoute: adminStatus', adminStatus, 'superAdminStatus', superAdminStatus);
      } catch (error) {
        console.error('AdminRoute: Error checking admin status', error);
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setPaywallOnly(false);
      } finally {
        setIsChecking(false);
        console.log('AdminRoute: Access check complete, isAdmin:', isAdmin, 'isSuperAdmin:', isSuperAdmin);
      }
    };

    checkAccess();
  }, []);

  console.log('AdminRoute: Rendering - isChecking:', isChecking, 'isAuthenticated:', isAuthenticated, 'isAdmin:', isAdmin, 'isSuperAdmin:', isSuperAdmin);

  if (isChecking) {
    return <NovaLoader label="Checking access" />;
  }
  
  if (!isAuthenticated) {
    console.log('AdminRoute: Redirecting to login');
    return <Navigate to="/login" replace />;
  }

  if (paywallOnly) {
    return (
      <SubscriptionPaywall
        title="Admin dashboard locked"
        subtitle="Your trial has ended. Subscribe to restore admin dashboard, uploads, and chat."
        onClose={() => {
          const base = (import.meta.env.VITE_BASE_PATH || '').replace(/\/$/, '');
          window.location.assign(`${base}/chat`);
        }}
      />
    );
  }
  
  if (!isAdmin && !isSuperAdmin) {
    console.log('AdminRoute: Not admin/superadmin, redirecting to home');
    return <Navigate to="/chat" replace />;
  }
  
  console.log('AdminRoute: Rendering children');
  return children;
}

function App() {
  // Basename: use VITE_BASE_PATH if set (e.g. /novabot for subpath), else '' for root (clean URL)
  let basename = import.meta.env.VITE_BASE_PATH ?? '';
  if (typeof basename === 'string') basename = basename.replace(/\/$/, '');
  
  return (
    <Router basename={basename}>
      <ConfirmHost />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route 
          path="/" 
          element={
            <LandingRoute>
              <LandingPage />
            </LandingRoute>
          } 
        />
        <Route 
          path="/chat" 
          element={
            <ProtectedRoute>
              <ChatPage />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/apps"
          element={
            <ProtectedRoute>
              <MyAppsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tools/sign-pdf"
          element={
            <ProtectedRoute>
              <SignPdfPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tools/pdf"
          element={
            <ProtectedRoute>
              <PdfToolsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tools/convert"
          element={
            <ProtectedRoute>
              <FileConvertPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/admin" 
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          } 
        />
        <Route 
          path="/metrics" 
          element={
            <AdminRoute>
              <MetricsDashboard />
            </AdminRoute>
          } 
        />
      </Routes>
    </Router>
  );
}

export default App;

